import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  Space,
  Table,
  Typography,
  message,
  Modal,
} from 'antd';
import PageTitle from '../../components/PageTitle';
import {
  byCloudGetMeterData,
  byCloudGatewayBind,
  byCloudGatewayClearArchive,
  byCloudGatewayGetResult,
  byCloudGatewayReadBound,
  byCloudGatewaySetArchive,
  byCloudGatewayUnbind,
  byCloudPickTaskId,
} from '../../api';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const POLL_TIMES = 12;
const POLL_MS = 1500;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function parseMeterList(raw) {
  return String(raw || '')
    .split(/[,，\s\n\r]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function extractMeterNos(body) {
  const d = (body && body.data) || {};
  const list = d.meterNos || d.meter_nos || d.meters || [];
  if (!Array.isArray(list)) return [];
  return list.map((x) => String(x || '').trim()).filter(Boolean);
}

/** remainingPowerTime 距现在 ≤6 小时 → 粗略「有上报」；文档无真正在线字段 */
function roughOnlineLabel(remainingPowerTime) {
  const t = Date.parse(String(remainingPowerTime || '').replace(/-/g, '/'));
  if (!Number.isFinite(t)) return { text: '未知', hoursAgo: null };
  const hours = (Date.now() - t) / 3600000;
  if (hours < 0) return { text: '有上报', hoursAgo: 0 };
  if (hours <= 6) return { text: '有上报', hoursAgo: hours };
  return { text: '超时未报', hoursAgo: hours };
}

function relayLabel(jdqzt) {
  const v = String(jdqzt);
  if (v === '0') return '合闸';
  if (v === '1') return '拉闸';
  return v === '' || v === 'undefined' || v === 'null' ? '-' : v;
}

function errMsg(e) {
  return (
    e?.response?.data?.error ||
    e?.response?.data?.vendor_msg ||
    e?.message ||
    String(e)
  );
}

async function pollGatewayResult(taskId, method, onTick) {
  let last = null;
  for (let i = 0; i < POLL_TIMES; i++) {
    if (onTick) onTick(i + 1, POLL_TIMES, taskId, method);
    try {
      last = await byCloudGatewayGetResult(taskId, method);
      if (last && (last.success === true || last.vendor_code === 0) && last.data) {
        return last;
      }
    } catch (e) {
      last = { success: false, error: errMsg(e), body: e?.response?.data || null };
    }
    await sleep(POLL_MS);
  }
  return last;
}

export default function ByGatewayBind() {
  const [gatewayNo, setGatewayNo] = useState('');
  const [childNos, setChildNos] = useState('');
  const [busy, setBusy] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [rows, setRows] = useState([]);
  const [lastRaw, setLastRaw] = useState(null);
  const [listRows, setListRows] = useState([]);
  const [listTotal, setListTotal] = useState(0);

  const childList = useMemo(() => parseMeterList(childNos), [childNos]);

  const needGateway = () => {
    const g = String(gatewayNo || '').trim();
    if (!g) {
      message.warning('请填写母表/网关编号（通常 172 开头）');
      return '';
    }
    return g;
  };

  const run = async (label, fn) => {
    if (busy) return;
    setBusy(true);
    setStatusText(label + '…');
    try {
      await fn();
    } catch (e) {
      message.error(errMsg(e));
      setStatusText(label + '失败：' + errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const queryBound = () =>
    run('查询已绑定子表', async () => {
      const g = needGateway();
      if (!g) return;
      const merged = new Map();
      const packs = [];
      for (const p1 of ['1', '2', '3', '4']) {
        setStatusText(`下发读绑定 p1=${p1}…`);
        const body = await byCloudGatewayReadBound(g, p1);
        packs.push({ p1, send: body });
        const tid = byCloudPickTaskId(body);
        if (!tid) {
          packs.push({ p1, note: '无 taskId，跳过轮询', body });
          continue;
        }
        const polled = await pollGatewayResult(tid, 'READ_METER', (i, n, id) => {
          setStatusText(`轮询 p1=${p1} ${i}/${n} taskId=${id}`);
        });
        packs.push({ p1, task_id: tid, poll: polled });
        extractMeterNos(polled).forEach((mn) => {
          if (!merged.has(mn)) merged.set(mn, { meter_no: mn, from_p1: p1 });
        });
      }
      const list = Array.from(merged.values());
      setRows(list);
      setLastRaw(packs);
      setStatusText(`查询完成，共 ${list.length} 块子表`);
      message.success(`已查到 ${list.length} 块子表`);
    });

  const doBind = () =>
    run('绑定子表', async () => {
      const g = needGateway();
      if (!g) return;
      if (!childList.length) {
        message.warning('请填写子表号（通常 161 开头，可多个）');
        return;
      }
      const body = await byCloudGatewayBind(g, childList);
      const tid = byCloudPickTaskId(body);
      let poll = null;
      if (tid) {
        poll = await pollGatewayResult(tid, 'READ_METER', (i, n, id) => {
          setStatusText(`绑定结果轮询 ${i}/${n} taskId=${id}`);
        });
      }
      setLastRaw({ step: 'bind', request: { gateway_no: g, meter_nos: childList }, response: body, poll });
      setStatusText('绑定下发完成');
      message.success('绑定已提交');
    });

  const doUnbind = () =>
    run('解绑子表', async () => {
      if (!childList.length) {
        message.warning('请填写要解绑的子表号');
        return;
      }
      const results = [];
      for (const mn of childList) {
        setStatusText(`解绑 ${mn}…`);
        const body = await byCloudGatewayUnbind(mn);
        const tid = byCloudPickTaskId(body);
        let poll = null;
        if (tid) {
          poll = await pollGatewayResult(tid, 'DEL_METER', (i, n, id) => {
            setStatusText(`解绑 ${mn} 轮询 ${i}/${n} taskId=${id}`);
          });
        }
        results.push({ meter_no: mn, response: body, poll });
      }
      setLastRaw({ step: 'unbind', results });
      setStatusText(`解绑完成：${childList.length} 块`);
      message.success('解绑已提交');
    });

  const doSetArchive = () => {
    const g = needGateway();
    if (!g) return;
    Modal.confirm({
      title: '下发网关全部绑定档案？',
      content: '将云端绑定名单同步到母表本地（厂家 SET_ALL_METER）。',
      onOk: () =>
        run('下发档案', async () => {
          const body = await byCloudGatewaySetArchive(g);
          const tid = byCloudPickTaskId(body);
          let poll = null;
          if (tid) {
            poll = await pollGatewayResult(tid, 'SET_ALL_METER', (i, n, id) => {
              setStatusText(`下发档案轮询 ${i}/${n} taskId=${id}`);
            });
          }
          setLastRaw({ step: 'set-archive', gateway_no: g, response: body, poll });
          setStatusText('下发档案完成');
          message.success('下发档案已提交');
        }),
    });
  };

  const doClearArchive = () => {
    const g = needGateway();
    if (!g) return;
    Modal.confirm({
      title: '清除母表本地全部绑定档案？',
      content: '危险操作：清除后需重新下发档案，子表近场组网会受影响。',
      okType: 'danger',
      onOk: () =>
        run('清除档案', async () => {
          const body = await byCloudGatewayClearArchive(g);
          const tid = byCloudPickTaskId(body);
          let poll = null;
          if (tid) {
            poll = await pollGatewayResult(tid, 'CLEAR_ALL_METER', (i, n, id) => {
              setStatusText(`清除档案轮询 ${i}/${n} taskId=${id}`);
            });
          }
          setLastRaw({ step: 'clear-archive', gateway_no: g, response: body, poll });
          setStatusText('清除档案完成');
          message.success('清除档案已提交');
        }),
    });
  };

  /** 文档一 getMeterData：云端缓存列表，不触发抄表；不填表号则拉项目下全部（分页拼齐） */
  const fetchDeviceList = () =>
    run('获取设备列表', async () => {
      const filterNo = String(gatewayNo || '').trim() || (childList.length === 1 ? childList[0] : '');
      const pageSize = 100;
      let page = 1;
      let total = 0;
      const all = [];
      const packs = [];
      for (;;) {
        setStatusText(`获取设备列表 第 ${page} 页…`);
        const body = await byCloudGetMeterData({
          meter_no: filterNo,
          measure_type: '1',
          page_number: page,
          page_size: pageSize,
        });
        packs.push({ page, body });
        const d = (body && body.data) || {};
        total = Number(d.totalCount != null ? d.totalCount : d.total || 0) || 0;
        const list = Array.isArray(d.list) ? d.list : [];
        list.forEach((item, idx) => {
          const mn = String(item.measureNo || item.meterNo || '').trim();
          const rt = item.remainingPowerTime || item.remaining_power_time || '';
          const online = roughOnlineLabel(rt);
          all.push({
            key: `${mn || 'row'}-${page}-${idx}`,
            measure_no: mn,
            measure_name: item.measureName || '',
            remaining_power: item.remainingPower,
            remaining_power_time: rt,
            zxygzdl: item.zxygzdl,
            jdqzt: relayLabel(item.jdqzt),
            rough_online: online.text,
            hours_ago: online.hoursAgo == null ? '-' : online.hoursAgo.toFixed(1),
            raw: item,
          });
        });
        if (!list.length || all.length >= total || page >= 50) break;
        page += 1;
      }
      setListRows(all);
      setListTotal(total || all.length);
      setLastRaw({ step: 'get-meter-data', filter_meter_no: filterNo || '(全部)', packs });
      setStatusText(
        `设备列表完成：本页合计 ${all.length} 条` +
          (total ? `（厂家 totalCount=${total}）` : '') +
          (filterNo ? `；过滤表号 ${filterNo}` : '；未填表号=项目全部')
      );
      message.success(`已获取 ${all.length} 条设备`);
    });

  const columns = [
    { title: '子表号', dataIndex: 'meter_no', key: 'meter_no' },
    { title: '来源分页 p1', dataIndex: 'from_p1', key: 'from_p1', width: 120 },
  ];

  const listColumns = [
    { title: '表号', dataIndex: 'measure_no', key: 'measure_no', width: 160 },
    { title: '剩余电量', dataIndex: 'remaining_power', key: 'remaining_power', width: 100 },
    { title: '度数', dataIndex: 'zxygzdl', key: 'zxygzdl', width: 90 },
    { title: '继电器', dataIndex: 'jdqzt', key: 'jdqzt', width: 80 },
    { title: '最新上报时间', dataIndex: 'remaining_power_time', key: 'remaining_power_time', width: 180 },
    {
      title: '粗略状态(≤6h有上报)',
      dataIndex: 'rough_online',
      key: 'rough_online',
      width: 150,
      render: (t, r) => (t === '有上报' ? `${t}（${r.hours_ago}h前）` : t === '超时未报' ? `${t}（${r.hours_ago}h前）` : t),
    },
  ];

  return (
    <div>
      <PageTitle title="BY 子母表绑定" />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="母表（网关）通常 172 开头，子表通常 161 开头。设备列表来自厂家云缓存，不触发抄表；「有上报」仅表示 remainingPowerTime 在约 6 小时内，不能当实时在线。"
      />
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong>母表 / 网关编号 gatewayNo</Text>
            <Input
              style={{ marginTop: 8 }}
              placeholder="例如 172xxxxxxxxxx"
              value={gatewayNo}
              onChange={(e) => setGatewayNo(e.target.value)}
              disabled={busy}
            />
          </div>
          <div>
            <Text strong>子表号（可多个，逗号/空格/换行分隔）</Text>
            <TextArea
              style={{ marginTop: 8 }}
              rows={3}
              placeholder="例如 161xxxxxxxxxx"
              value={childNos}
              onChange={(e) => setChildNos(e.target.value)}
              disabled={busy}
            />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            <Button type="primary" loading={busy} onClick={queryBound}>
              查询已绑定
            </Button>
            <Button type="primary" loading={busy} onClick={doBind} style={{ background: '#52c41a', borderColor: '#52c41a' }}>
              绑定
            </Button>
            <Button danger loading={busy} onClick={doUnbind}>
              解绑
            </Button>
            <span style={{ width: 28 }} />
            <Button loading={busy} onClick={doSetArchive}>
              下发档案
            </Button>
            <Button danger loading={busy} onClick={doClearArchive}>
              清除档案
            </Button>
            <span style={{ width: 28 }} />
            <Button loading={busy} onClick={fetchDeviceList}>
              获取设备列表
            </Button>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            「获取设备列表」：母表栏有值则按该表号查；否则若只填了一个子表号则按子表查；都空则拉项目下全部电表。
          </Text>
          {statusText ? <Paragraph type="secondary">{statusText}</Paragraph> : null}
        </Space>
      </Card>

      <Card size="small" title={`厂家设备列表（${listRows.length}${listTotal ? ` / total ${listTotal}` : ''}）`} style={{ marginBottom: 16 }}>
        <Table
          rowKey="key"
          size="small"
          pagination={{ pageSize: 20 }}
          columns={listColumns}
          dataSource={listRows}
          locale={{ emptyText: '暂无数据，请点「获取设备列表」' }}
          scroll={{ x: 900 }}
        />
      </Card>

      <Card size="small" title={`已绑定子表（${rows.length}）`} style={{ marginBottom: 16 }}>
        <Table
          rowKey="meter_no"
          size="small"
          pagination={false}
          columns={columns}
          dataSource={rows}
          locale={{ emptyText: '暂无数据，请先查询' }}
        />
      </Card>

      {lastRaw ? (
        <Card size="small" title="最近一次原始响应（排查用）">
          <pre style={{ margin: 0, maxHeight: 360, overflow: 'auto', fontSize: 12 }}>
            {JSON.stringify(lastRaw, null, 2)}
          </pre>
        </Card>
      ) : null}
    </div>
  );
}
